/**
 * 토큰 저장소 — 클라이언트 전용.
 *
 * 보관은 **localStorage** 다. sessionStorage 는 탭 단위라 탭을 닫거나 새 탭을 열면 그 즉시
 * 로그인이 풀렸다 — 콘솔은 한 번 붙잡고 오래 편집하는 화면이라 이게 계속 재로그인으로 이어졌다.
 * 게이트웨이가 refresh 로 세션을 길게 이어주는데도 저장소가 그걸 못 받쳐준 셈.
 *
 * 최종 목표는 httpOnly cookie + refresh 로테이션(PR4)이다. 그전까지 localStorage 라
 * XSS 에 토큰이 노출되는 위험은 sessionStorage 와 동일한 급으로 남아 있다.
 */
import { decodeJwt } from '@/@shared/utils/jwt';

const ACCESS_KEY = 'stepin.access_token';

/**
 * 로컬 개발용 통과 토큰.
 *
 * `useAuthGuard`·`useSession` 이 이 값을 알아보고 로그인 화면을 건너뛴다 —
 * dev 브릿지(`NEXT_PUBLIC_BUSINESS_DEV_BRIDGE`)가 켜진 로컬에서만이고, API 호출은
 * 테스트 패스포트를 쓴다. **JWT 가 아니므로 아래 정리 규칙에 걸려 사라진다** — 그래서 여기서
 * 한 번 예외로 인정한다. 예외를 안 두면 두 훅의 분기는 절대 참이 되지 않는 죽은 코드가 되고,
 * 화면을 열어 보기 전에는 죽은 줄도 모른다.
 *
 * 브릿지가 꺼진 곳(=배포된 콘솔)에서는 그냥 깨진 값이라 버려진다.
 */
const DEMO_TOKEN = '__DEMO__';
const DEV_BRIDGE = process.env.NEXT_PUBLIC_BUSINESS_DEV_BRIDGE === 'true';
const REFRESH_KEY = 'stepin.refresh_token';
// 게이트웨이 JWT에 email 클레임이 없어서, 로그인 시점에 확보한 계정 이메일을 함께 보관한다(표시용).
const EMAIL_KEY = 'stepin.account_email';

/**
 * 저장소를 못 쓸 때의 **바닥값**이다(사생활 보호 모드 등). 평소에는 읽지 않는다.
 *
 * 예전에는 이 메모리가 **먼저**였다(`memoryAccess ?? read(...)`). 그래서 탭이 둘이면 조용히
 * 깨졌다: 탭 A 가 토큰을 회전시켜 localStorage 를 새 값으로 바꿔도, 탭 B 는 자기 메모리에 든
 * **옛 토큰**을 계속 내밀었다. 저장소는 공유돼도 메모리는 탭마다 따로이기 때문이다.
 *
 * 그 뒤가 더 나빴다. 옛 토큰이 401 을 받으면 미들웨어는 "보낼 때 토큰(`sent`) 과 지금 토큰(`now`)
 * 이 같은가"로 **내 세션의 401 인지**를 가리는데, 메모리가 안 바뀌니 언제나 같았다. 그래서
 * 남이 회전시킨 탓에 죽은 요청을 자기 세션의 죽음으로 읽고 **세션을 통째로 비웠다** — 탭 하나가
 * 나머지 탭 전부를 로그아웃시켰다. 로그인한 지 1분 만에 튕기는 모양이 이것이다(2026-08-27 dev).
 *
 * 그래서 **정본은 저장소**다. 탭이 몇이든 같은 값을 본다.
 */
let memoryAccess: string | null = null;
let memoryRefresh: string | null = null;
let memoryEmail: string | null = null;
let hydrated = false;

const isBrowser = typeof window !== 'undefined';

function read(key: string): string | null {
	if (!isBrowser) return null;
	try {
		return window.localStorage.getItem(key);
	} catch {
		return null;
	}
}

function write(key: string, value: string | null) {
	if (!isBrowser) return;
	try {
		if (value === null) {
			window.localStorage.removeItem(key);
		} else {
			window.localStorage.setItem(key, value);
		}
	} catch {
		// ignore (private mode etc.)
	}
}

/**
 * 저장소를 localStorage 로 옮기기 전에 로그인해 둔 세션을 살려 준다 — 이 변경 자체가
 * 한 번의 강제 로그아웃이 되지 않도록, 남아 있는 sessionStorage 값을 한 번만 옮겨 담는다.
 * (다음 배포 이후 지워도 되는 코드)
 */
function migrateFromSessionStorage() {
	if (hydrated || !isBrowser) return;
	hydrated = true;
	try {
		for (const key of [ACCESS_KEY, REFRESH_KEY, EMAIL_KEY]) {
			const legacy = window.sessionStorage.getItem(key);
			if (legacy && !window.localStorage.getItem(key)) window.localStorage.setItem(key, legacy);
			if (legacy) window.sessionStorage.removeItem(key);
		}
	} catch {
		// ignore
	}
}

/**
 * 헤더에서 꺼낸 값을 **JWT 한 개**로 정리한다.
 *
 * 헤더는 값이 하나라는 보장이 없다 — 같은 이름이 두 번 실리면 `Headers.get()` 이 둘을
 * **쉼표로 이어붙인 한 문자열**로 준다(`eyJ…A, eyJ…B`). 그대로 저장하면 이후 모든 요청이 JWT 가
 * 아닌 값을 들고 나가고, 게이트웨이는 만료가 아니라 **서명 검증 실패**로 답한다. 실제로
 * `REFRESHTOKEN_INVALID` 가 그렇게 났다(2026-08-12 dev — 쉼표로 이은 값으로 같은 응답 재현).
 * `Bearer ` 접두사·앞뒤 공백도 같은 부류라 함께 털어낸다.
 *
 * JWT 모양(점 두 개로 나뉜 세 조각)이 아니면 버린다 — 깨진 값을 들고 도는 것보다 없는 편이 낫다.
 */
function sanitizeToken(raw: string | null): string | null {
	if (!raw) return null;
	const first = raw.split(',')[0].trim().replace(/^Bearer\s+/i, '');
	if (first === DEMO_TOKEN && DEV_BRIDGE) return first;
	if (!/^[\w-]+\.[\w-]+\.[\w-]+$/.test(first)) {
		if (raw.trim()) console.warn('[auth] 토큰이 JWT 형태가 아니라 버립니다.', { length: raw.length });
		return null;
	}
	return first;
}

/** 남은 수명이 이보다 짧으면 갱신이 필요하다고 본다(경계에서 한 번 헛도는 것을 피한다). */
const RENEW_SKEW_MS = 60_000;

/** JWT 의 만료 시각(ms). 못 읽으면 null. */
const expiryMs = (token: string | null): number | null => {
	const exp = decodeJwt(token)?.exp;
	return typeof exp === 'number' ? exp * 1000 : null;
};

/**
 * 진행 중인 갱신 — **앱 전체에서 하나뿐이다.**
 *
 * WEB 토큰의 refreshToken 은 auth 가 Redis 에 `web:{userId}` 키로 **하나만** 들고 있고, 갱신 때
 * 제시한 값이 저장된 것과 다르면 거부한다(`refreshTokenMismatchException` → `REFRESHTOKEN_INVALID`).
 * 새 토큰이 발급되면 그 자리에 덮어써지므로, **이전 refreshToken 은 그 순간 무효**가 된다.
 *
 * 그래서 동시에 두 번 갱신하면 안 된다 — 먼저 도착한 쪽이 저장 값을 바꾸고, 뒤늦은 쪽은 이미
 * 무효가 된 값을 내밀어 401 을 받는다. 화면 하나가 열릴 때 요청이 5~6개씩 나가므로 실제로 난다.
 *
 * 잠금을 저장소에 두는 이유: 클라이언트마다 따로 두면 서로를 못 막는다. business·tournament·
 * 공통 미들웨어가 **같은 잠금**을 봐야 한다.
 *
 * 그런데 모듈 변수는 **탭 안에서만** 잠금이다. 콘솔을 두 탭에 띄워 두는 건 흔한 일이고, 그때
 * 두 탭이 동시에 갱신하면 뒤늦은 쪽의 refreshToken 은 이미 무효다. 그래서 잠금을 localStorage
 * 에도 남겨 **탭 사이에서도** 한 번만 돌게 한다.
 *
 * (BUSINESS issuer 토큰은 Redis 를 타지 않아 이 제약이 없다 — 어드민 개발자 토큰이 그 경우다.)
 */
let renewing: Promise<void> | null = null;
let releaseRenewal: (() => void) | null = null;
let safety: ReturnType<typeof setTimeout> | null = null;

/** 갱신 잠금이 사고로 남았을 때 스스로 풀리는 시간. */
const RENEW_LOCK_TIMEOUT_MS = 15_000;

/** 탭 사이의 갱신 잠금 — 값은 «언제 잡았나»(ms). 탭이 죽어 남더라도 시간이 지나면 무시된다. */
const LOCK_KEY = 'stepin.renew_lock';

/** 다른 탭이 지금 갱신을 들고 있는가. */
function lockedByOtherTab(): boolean {
	const at = Number(read(LOCK_KEY));
	return Number.isFinite(at) && at > 0 && Date.now() - at < RENEW_LOCK_TIMEOUT_MS;
}

export const tokenStore = {
	getAccessToken(): string | null {
		migrateFromSessionStorage();
		// 읽을 때도 정리한다 — 이미 오염된 값이 저장된 브라우저를 재로그인 없이 되살린다.
		return sanitizeToken(read(ACCESS_KEY) ?? memoryAccess);
	},
	getRefreshToken(): string | null {
		migrateFromSessionStorage();
		return sanitizeToken(read(REFRESH_KEY) ?? memoryRefresh);
	},
	setTokens(access: string | null, refresh: string | null) {
		const a = sanitizeToken(access);
		const r = sanitizeToken(refresh);
		memoryAccess = a;
		memoryRefresh = r;
		write(ACCESS_KEY, a);
		write(REFRESH_KEY, r);
	},
	/**
	 * 게이트웨이는 토큰을 갱신했을 때 **응답 헤더로 새 access/refresh 쌍**을 내려준다.
	 * 있으면 회전시켜 저장한다. 헤더 이름 대소문자는 서버·프록시마다 달라서 둘 다 본다.
	 *
	 * 판독을 여기 한 곳에 둔다 — 예전엔 middleware·businessClient·세션 복구가 각자 읽어서,
	 * 한쪽만 고치면 나머지가 조용히 옛 토큰을 들고 있었다.
	 *
	 * @returns 회전이 실제로 일어났으면 true
	 */
	absorbRotation(res: Response): boolean {
		const access = sanitizeToken(res.headers.get('accesstoken') ?? res.headers.get('Accesstoken'));
		const refresh = sanitizeToken(res.headers.get('refreshtoken') ?? res.headers.get('Refreshtoken'));
		if (!access || !refresh) return false;

		/*
		 * **토큰은 뒤로 가지 않는다.**
		 *
		 * 응답은 보낸 순서대로 오지 않는다. 먼저 나갔던 요청이 늦게 도착하면 그 응답에 실린 옛
		 * 토큰이 방금 받은 새 토큰을 덮어쓴다. 실제로 로그인 직후 `/business/mine` 은 수명 1799초
		 * 짜리로 통과했는데, 조직에 들어가는 순간 저장소가 **6시간 전에 만료된 토큰**으로 바뀌어
		 * 있었다(2026-08-12 dev). 그 만료 토큰의 refresh 로 갱신을 시도하니 `REFRESHTOKEN_INVALID`
		 * 가 났고, 세션이 지워져 뒤따르던 요청 다섯 개가 전부 `NO_AUTHORITY` 로 떨어졌다.
		 *
		 * 들어온 토큰이 지금 것보다 오래됐으면 버린다. 어디서 왔는지는 경고로 남긴다.
		 */
		const incoming = expiryMs(access);
		const current = expiryMs(this.getAccessToken());
		if (incoming !== null && current !== null && incoming < current) {
			console.warn('[auth] 더 오래된 토큰이 응답에 실려와 무시합니다.', {
				지금: new Date(current).toISOString(),
				들어온것: new Date(incoming).toISOString(),
			});
			return false;
		}

		this.setTokens(access, refresh);
		return true;
	},
	/**
	 * **지금 갱신이 필요한가** — accessToken 이 만료됐거나 곧 만료된다.
	 *
	 * 이때만 요청에 refreshToken 을 함께 싣는다. 평소에는 accessToken 만 보낸다 — 갱신이 필요
	 * 없는데 리프레시까지 매번 실어 보낼 이유가 없다.
	 */
	needsRenewal(skewMs: number = RENEW_SKEW_MS): boolean {
		const exp = expiryMs(this.getAccessToken());
		if (exp === null) return false; // 못 읽으면 서버 판정에 맡긴다
		return exp - Date.now() <= skewMs;
	},

	/** refreshToken 이 아직 살아 있는가 — 죽었으면 다시 로그인하는 수밖에 없다. */
	canRenew(): boolean {
		const exp = expiryMs(this.getRefreshToken());
		return exp !== null && exp > Date.now();
	},

	/**
	 * 갱신 잠금을 잡아 본다.
	 * @returns true 면 **이 요청이 refreshToken 을 들고 간다** — 끝나면 반드시 `endRenewal()`.
	 */
	tryBeginRenewal(): boolean {
		if (renewing) return false;
		// 다른 탭이 이미 들고 있으면 양보한다 — 둘이 동시에 내밀면 뒤늦은 쪽이 무효 처리된다.
		if (lockedByOtherTab()) return false;
		write(LOCK_KEY, String(Date.now()));
		let release!: () => void;
		renewing = new Promise<void>((resolve) => {
			release = resolve;
		});
		releaseRenewal = () => {
			if (safety !== null) clearTimeout(safety);
			safety = null;
			releaseRenewal = null;
			renewing = null;
			write(LOCK_KEY, null); // 기다리는 탭을 놓아 준다
			release();
		};
		/*
		 * 안전장치 — 요청이 네트워크 오류로 끝나 `endRenewal()` 이 불리지 않으면 잠금이 영원히
		 * 남고 이후 모든 요청이 갱신을 못 한다. 시간이 지나면 스스로 푼다.
		 */
		safety = setTimeout(() => releaseRenewal?.(), RENEW_LOCK_TIMEOUT_MS);
		return true;
	},

	/** 잠금을 놓는다 — `tryBeginRenewal()` 이 true 를 준 요청만 부른다. */
	endRenewal(): void {
		releaseRenewal?.();
	},

	/** 다른 요청이 갱신 중이면 끝나기를 기다린다 — 그 뒤엔 새 accessToken 으로 가면 된다. */
	async waitForRenewal(): Promise<void> {
		if (renewing) {
			await renewing.catch(() => {});
			return;
		}
		/*
		 * 갱신을 든 것이 **다른 탭**이면 기다릴 약속(Promise)이 이쪽에 없다. 저장소의 잠금이
		 * 풀리기를 지켜본다 — 풀리는 순간 새 토큰은 이미 저장소에 들어와 있다.
		 * (`storage` 이벤트 대신 폴링인 이유: 잠금이 남은 채 탭이 죽어도 시간으로 빠져나온다.)
		 */
		const until = Date.now() + RENEW_LOCK_TIMEOUT_MS;
		while (lockedByOtherTab() && Date.now() < until) {
			await new Promise((r) => setTimeout(r, 60));
		}
	},

	/**
	 * 갱신을 **한 번만** 돌린다(요청을 직접 들고 있는 클라이언트용 래퍼).
	 *
	 * @param run refreshToken 을 실어 보내는 실제 요청.
	 * @returns `carried` 이 요청이 갱신을 직접 들고 갔는가.
	 *          `false` 면 다른 요청의 갱신을 기다린 것이므로, **새 accessToken 으로 그냥 다시 쏘면 된다.**
	 */
	async renewOnce<T>(run: () => Promise<T>): Promise<{ carried: boolean; result?: T }> {
		if (!this.tryBeginRenewal()) {
			await this.waitForRenewal();
			return { carried: false };
		}
		try {
			return { carried: true, result: await run() };
		} finally {
			this.endRenewal();
		}
	},

	getAccountEmail(): string | null {
		migrateFromSessionStorage();
		return read(EMAIL_KEY) ?? memoryEmail;
	},
	setAccountEmail(email: string | null) {
		memoryEmail = email;
		write(EMAIL_KEY, email);
	},
	clear() {
		memoryAccess = null;
		memoryRefresh = null;
		memoryEmail = null;
		write(ACCESS_KEY, null);
		write(REFRESH_KEY, null);
		write(EMAIL_KEY, null);
		// 잠금까지 걷는다 — 남겨 두면 다시 로그인한 세션의 첫 갱신이 최대 15초 묶인다.
		write(LOCK_KEY, null);
	},
};
